import React, { useState, useEffect } from 'react';
import clientDB from '../config/clientDatabase';
import './TeamInvitation.css';

const TeamInvitation = ({ user, company }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteName, setInviteName] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (company) {
      loadTeamData();
    }
  }, [company]);

  const loadTeamData = () => {
    if (!company) return;

    // Load current team members
    const members = clientDB.getCompanyUsers(company.id);
    setTeamMembers(members);

    // Load pending invitations
    const invitations = clientDB.getPendingInvitations(company.id);
    setPendingInvitations(invitations);
  };

  const canInvite = () => {
    const userEmail = user?.attributes?.email || user?.username;
    return clientDB.canInviteUsers(userEmail);
  };

  const sendInvitation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const userEmail = user?.attributes?.email || user?.username;

      // Check if user already exists
      const existingUser = clientDB.getUser(inviteEmail);
      if (existingUser && existingUser.companyId === company.id) {
        setMessage({ type: 'error', text: 'This user is already a team member.' });
        setLoading(false);
        return;
      }

      // Check if there's already a pending invitation
      const existingInvite = pendingInvitations.find(inv => inv.inviteeEmail === inviteEmail);
      if (existingInvite) {
        setMessage({ type: 'error', text: 'An invitation has already been sent to this email.' });
        setLoading(false);
        return;
      }

      // Check team member limit
      const currentMemberCount = teamMembers.length + pendingInvitations.length;
      if (company.settings.maxTeamMembers > 0 && currentMemberCount >= company.settings.maxTeamMembers) {
        setMessage({
          type: 'error',
          text: `Team member limit (${company.settings.maxTeamMembers}) reached. Please upgrade or remove existing members.`
        });
        setLoading(false);
        return;
      }

      // Create invitation
      const inviteId = clientDB.createInvitation(userEmail, inviteEmail, company.id, inviteRole);

      // In a real app, this would send an email
      // For now, we'll just show the invitation link
      const inviteLink = `${window.location.origin}/invite/${inviteId}`;

      setMessage({
        type: 'success',
        text: `Invitation sent to ${inviteEmail}`,
        link: inviteLink
      });

      // Reset form
      setInviteEmail('');
      setInviteName('');
      setInviteRole('viewer');

      // Reload data
      loadTeamData();

    } catch (error) {
      console.error('Error sending invitation:', error);
      setMessage({ type: 'error', text: 'Failed to send invitation. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const removeTeamMember = (email) => {
    if (window.confirm(`Are you sure you want to remove ${email} from the team?`)) {
      if (clientDB.removeUser(email)) {
        setMessage({ type: 'success', text: `${email} has been removed from the team.` });
        loadTeamData();
      } else {
        setMessage({ type: 'error', text: 'Failed to remove team member.' });
      }
    }
  };

  const changeRole = (email, newRole) => {
    if (clientDB.updateUserRole(email, newRole)) {
      setMessage({ type: 'success', text: `Role updated for ${email}.` });
      loadTeamData();
    } else {
      setMessage({ type: 'error', text: 'Failed to update role.' });
    }
  };

  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      const tempMessage = { ...message };
      setMessage({ type: 'info', text: 'Invitation link copied to clipboard!' });
      setTimeout(() => setMessage(tempMessage), 2000);
    });
  };

  if (!canInvite()) {
    return (
      <div className="team-invitation-container">
        <div className="no-permission">
          <h2>Team Management</h2>
          <p>You don't have permission to manage team members.</p>
          <p>Only company owners and administrators can invite team members.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-invitation-container">
      <div className="team-header">
        <h2>Team Management</h2>
        <p>Manage your team members and send invitations</p>
      </div>

      {/* Invitation Form */}
      <div className="invitation-card">
        <h3>Invite New Team Member</h3>
        <form onSubmit={sendInvitation} className="invitation-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="inviteEmail">Email Address</label>
              <input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="inviteName">Name</label>
              <input
                id="inviteName"
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="John Smith"
              />
            </div>

            <div className="form-group">
              <label htmlFor="inviteRole">Role</label>
              <select
                id="inviteRole"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="viewer">Viewer (Read Only)</option>
                <option value="admin">Admin (Full Access)</option>
              </select>
            </div>
          </div>

          <button type="submit" className="invite-button" disabled={loading}>
            {loading ? 'Sending...' : 'Send Invitation'}
          </button>
        </form>

        {message && (
          <div className={`message ${message.type}`}>
            <p>{message.text}</p>
            {message.link && (
              <div className="invite-link-container">
                <p>Share this link with the invitee:</p>
                <div className="invite-link">
                  <code>{message.link}</code>
                  <button
                    onClick={() => copyInviteLink(message.link)}
                    className="copy-button"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Team Members */}
      <div className="team-members-card">
        <h3>Current Team Members ({teamMembers.length})</h3>
        {company.settings.maxTeamMembers > 0 && (
          <p className="member-limit">
            Limit: {teamMembers.length} / {company.settings.maxTeamMembers} members
          </p>
        )}

        <div className="members-list">
          {teamMembers.map(member => (
            <div key={member.email} className="member-item">
              <div className="member-info">
                <div className="member-name">{member.name || member.email}</div>
                <div className="member-email">{member.email}</div>
                <div className="member-joined">Joined: {member.joinedDate}</div>
              </div>

              <div className="member-actions">
                <select
                  value={member.role}
                  onChange={(e) => changeRole(member.email, e.target.value)}
                  className="role-selector"
                  disabled={member.role === 'owner'}
                >
                  <option value="owner" disabled>Owner</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>

                {member.role !== 'owner' && (
                  <button
                    onClick={() => removeTeamMember(member.email)}
                    className="remove-button"
                    title="Remove team member"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="pending-invitations-card">
          <h3>Pending Invitations ({pendingInvitations.length})</h3>
          <div className="invitations-list">
            {pendingInvitations.map(invitation => (
              <div key={invitation.id} className="invitation-item">
                <div className="invitation-info">
                  <div className="invitation-email">{invitation.inviteeEmail}</div>
                  <div className="invitation-details">
                    Role: {invitation.role} •
                    Sent: {new Date(invitation.createdDate).toLocaleDateString()} •
                    Expires: {new Date(invitation.expiryDate).toLocaleDateString()}
                  </div>
                </div>

                <div className="invitation-actions">
                  <button
                    onClick={() => {
                      const link = `${window.location.origin}/invite/${invitation.id}`;
                      copyInviteLink(link);
                    }}
                    className="resend-button"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamInvitation;